import { useNotification } from '@/common/composables/useNotification'

export type FormLayoutElementRef = {
  id: number | null
  name: string
  type: string
  panelId: string | null
}

export type FormElementNameConflict = {
  name: string
  elements: FormLayoutElementRef[]
}

const reportedConflictKeys = new Set<string>()

const tryParseElementId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = parseInt(value.trim(), 10)
    return Number.isFinite(parsed) ? parsed : null
  }
  return null
}

const readElementName = (config: Record<string, unknown>): string => {
  const fromChild = (config.name as { _text?: unknown } | undefined)?._text
  if (typeof fromChild === 'string' && fromChild.trim()) {
    return fromChild.trim()
  }
  const fromAttr = config._attributes
  if (fromAttr && typeof fromAttr === 'object' && 'name' in fromAttr) {
    const normalized = String((fromAttr as { name?: unknown }).name ?? '').trim()
    if (normalized) return normalized
  }
  return ''
}

const readElementType = (config: Record<string, unknown>): string => {
  const typeText = (config.type as { _text?: unknown } | undefined)?._text
  return typeof typeText === 'string' ? typeText.trim() : ''
}

const resolveFormLayoutRoot = (formRoot: unknown): Record<string, unknown> | null => {
  if (!formRoot || typeof formRoot !== 'object') return null
  const root = formRoot as Record<string, unknown>
  if (root.panels != null || root.actions != null || root.hidden != null) return root
  const nested = root.form
  if (nested && typeof nested === 'object') {
    return nested as Record<string, unknown>
  }
  return root
}

/** Panel id from compact cell `panel` / `conditionalPanel` (как resolveLayoutPanelRef). */
const readCellPanelId = (cell: unknown): string | null => {
  if (!cell || typeof cell !== 'object') return null
  const record = cell as Record<string, unknown>
  const ref = (record.panel ?? record.conditionalPanel) as Record<string, unknown> | undefined
  if (!ref || typeof ref !== 'object') return null
  const panelIdRaw = ref._text
  if (panelIdRaw == null) return null
  const panelId = String(panelIdRaw).trim()
  return panelId || null
}

/** Только `conditionalPanel` (не обычный `panel`). */
const readCellConditionalPanelId = (cell: unknown): string | null => {
  if (!cell || typeof cell !== 'object') return null
  const record = cell as Record<string, unknown>
  const ref = record.conditionalPanel as Record<string, unknown> | undefined
  if (!ref || typeof ref !== 'object') return null
  const panelIdRaw = ref._text
  if (panelIdRaw == null) return null
  const panelId = String(panelIdRaw).trim()
  return panelId || null
}

const readConditionalPanelRefAttributes = (
  cell: unknown,
): Record<string, unknown> | null => {
  if (!cell || typeof cell !== 'object') return null
  const record = cell as Record<string, unknown>
  const ref = record.conditionalPanel as Record<string, unknown> | undefined
  if (!ref || typeof ref !== 'object') return null
  const attrs = ref._attributes
  if (!attrs || typeof attrs !== 'object') return null
  return attrs as Record<string, unknown>
}

/**
 * `subPanels="4524;1;-;4620;1"` → panel ids `4524`, `4620`.
 * Формат: пары `panelId;flag`, группы разделены токеном `-`.
 */
export function parseSubPanelsPanelIds(subPanelsRaw: unknown): string[] {
  const text = String(subPanelsRaw ?? '').trim()
  if (!text) return []
  const result: string[] = []
  const tokens = text.split(';').map((token) => token.trim())
  let index = 0
  while (index < tokens.length) {
    const part = tokens[index]
    if (!part || part === '-') {
      index += 1
      continue
    }
    if (/^\d+$/.test(part)) {
      result.push(part)
      index += 1
      // skip visibility flag after panel id
      if (index < tokens.length && /^\d+$/.test(tokens[index] ?? '')) {
        index += 1
      }
      continue
    }
    index += 1
  }
  return result
}

const addConditionalPanelWithBranches = (
  result: Set<string>,
  panelId: string,
  attributes: Record<string, unknown> | null | undefined,
): void => {
  if (!panelId) return
  result.add(panelId)
  if (!attributes) return
  for (const branchId of parseSubPanelsPanelIds(attributes.subPanels)) {
    result.add(branchId)
  }
}

const forEachCompactLayoutCell = (
  layout: Record<string, unknown>,
  visit: (cell: unknown) => void,
): void => {
  const visitTrContainer = (container: unknown): void => {
    if (!container || typeof container !== 'object') return
    const trRaw = (container as Record<string, unknown>).tr
    const rows = trRaw == null ? [] : Array.isArray(trRaw) ? trRaw : [trRaw]
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue
      const tdRaw = (row as Record<string, unknown>).td
      const cells = tdRaw == null ? [] : Array.isArray(tdRaw) ? tdRaw : [tdRaw]
      for (const cell of cells) visit(cell)
    }
  }

  const visitPages = (pagesRaw: unknown): void => {
    if (pagesRaw == null) return
    const pages = Array.isArray(pagesRaw) ? pagesRaw : [pagesRaw]
    for (const page of pages) visitTrContainer(page)
  }

  const visitRegion = (region: unknown): void => {
    if (!region || typeof region !== 'object') return
    visitPages((region as Record<string, unknown>).page)
    // После getForm region может быть уже развёрнут; compact page остаётся только до parse.
    visitTrContainer(region)
  }

  for (const key of ['main', 'header', 'footer'] as const) {
    visitRegion(layout[key])
  }

  const overlays = layout.overlays
  if (overlays && typeof overlays === 'object') {
    const overlayRaw = (overlays as Record<string, unknown>).overlay
    const overlayNodes = overlayRaw == null
      ? []
      : Array.isArray(overlayRaw)
        ? overlayRaw
        : [overlayRaw]
    for (const overlay of overlayNodes) visitTrContainer(overlay)
  }

  const hidden = layout.hidden
  if (hidden && typeof hidden === 'object') {
    const overlayRaw = (hidden as Record<string, unknown>).overlay
    const overlayNodes = overlayRaw == null
      ? []
      : Array.isArray(overlayRaw)
        ? overlayRaw
        : [overlayRaw]
    for (const overlay of overlayNodes) visitTrContainer(overlay)
  }
}

/**
 * Id панелей из `<hidden><overlay>` (сырой compact xml-js после getForm).
 * Их row-элементы не участвуют в проверке уникальности name.
 */
export function collectHiddenPanelIds(formRoot: unknown): Set<string> {
  const result = new Set<string>()
  const layout = resolveFormLayoutRoot(formRoot)
  const hidden = layout?.hidden
  if (!hidden || typeof hidden !== 'object') return result

  const overlayNodesRaw = (hidden as Record<string, unknown>).overlay
  const overlayNodes = overlayNodesRaw == null
    ? []
    : Array.isArray(overlayNodesRaw)
      ? overlayNodesRaw
      : [overlayNodesRaw]

  for (const overlay of overlayNodes) {
    if (!overlay || typeof overlay !== 'object') continue
    const trRaw = (overlay as Record<string, unknown>).tr
    const rows = trRaw == null ? [] : Array.isArray(trRaw) ? trRaw : [trRaw]
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue
      const tdRaw = (row as Record<string, unknown>).td
      const cells = tdRaw == null ? [] : Array.isArray(tdRaw) ? tdRaw : [tdRaw]
      for (const cell of cells) {
        const panelId = readCellPanelId(cell)
        if (panelId) result.add(panelId)
      }
    }
  }

  return result
}

const addPanelIdsFromCompactOverlayNode = (
  result: Set<string>,
  overlay: Record<string, unknown>,
): void => {
  const trRaw = overlay.tr
  const rows = trRaw == null ? [] : Array.isArray(trRaw) ? trRaw : [trRaw]
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue
    const tdRaw = (row as Record<string, unknown>).td
    const cells = tdRaw == null ? [] : Array.isArray(tdRaw) ? tdRaw : [tdRaw]
    for (const cell of cells) {
      const panelId = readCellPanelId(cell)
      if (panelId) result.add(panelId)
    }
  }
}

const addPanelIdsFromOverlayPanelRows = (
  result: Set<string>,
  panelRows: unknown,
): void => {
  if (!Array.isArray(panelRows)) return
  for (const row of panelRows) {
    if (!Array.isArray(row)) continue
    for (const cell of row) {
      if (!cell || typeof cell !== 'object') continue
      if ((cell as { _isRowspanPlaceholder?: unknown })._isRowspanPlaceholder) continue
      const panelId = String((cell as { panelId?: unknown }).panelId ?? '').trim()
      if (panelId) result.add(panelId)
    }
  }
}

const readOverlayNodeId = (overlay: Record<string, unknown>): string => {
  const fromDirect = overlay.id
  if (fromDirect != null && String(fromDirect).trim()) return String(fromDirect).trim()
  const fromAttrs = (overlay._attributes as { id?: unknown } | undefined)?.id
  return fromAttrs != null ? String(fromAttrs).trim() : ''
}

/**
 * Панели каждой form XML overlay-страницы (`<overlays><overlay>`).
 * Parsed: `form.overlays[]` + `panelRows[].panelId`. Compact xml-js: `overlays.overlay` + `tr/td/panel`.
 */
export function collectOverlayPagePanelIds(formRoot: unknown): Map<string, Set<string>> {
  const result = new Map<string, Set<string>>()
  const layout = resolveFormLayoutRoot(formRoot)
  const overlays = layout?.overlays
  if (overlays == null) return result

  const addOverlay = (overlayId: string, panelIds: Set<string>) => {
    if (!overlayId || panelIds.size === 0) return
    const existing = result.get(overlayId) ?? new Set<string>()
    for (const panelId of panelIds) existing.add(panelId)
    result.set(overlayId, existing)
  }

  if (Array.isArray(overlays)) {
    for (const overlay of overlays) {
      if (!overlay || typeof overlay !== 'object') continue
      const rec = overlay as Record<string, unknown>
      const panelIds = new Set<string>()
      if (Array.isArray(rec.panelRows)) {
        addPanelIdsFromOverlayPanelRows(panelIds, rec.panelRows)
      } else {
        addPanelIdsFromCompactOverlayNode(panelIds, rec)
      }
      addOverlay(readOverlayNodeId(rec), panelIds)
    }
    return result
  }

  if (typeof overlays !== 'object') return result

  const overlayRaw = (overlays as Record<string, unknown>).overlay
  const overlayNodes = overlayRaw == null
    ? []
    : Array.isArray(overlayRaw)
      ? overlayRaw
      : [overlayRaw]
  for (const overlay of overlayNodes) {
    if (!overlay || typeof overlay !== 'object') continue
    const rec = overlay as Record<string, unknown>
    const panelIds = new Set<string>()
    addPanelIdsFromCompactOverlayNode(panelIds, rec)
    addOverlay(readOverlayNodeId(rec), panelIds)
  }

  return result
}

/**
 * Имена полей overlay-страниц: fieldName → xml id overlay (`38683`, `38693`).
 * Дубликат имени на двух страницах живёт и после unmount lazy-диалога.
 */
export function collectOverlayFieldNamePages(formRoot: unknown): Map<string, string[]> {
  const overlayPanels = collectOverlayPagePanelIds(formRoot)
  const panelToOverlays = new Map<string, string[]>()
  for (const [overlayId, panelIds] of overlayPanels) {
    for (const panelId of panelIds) {
      const list = panelToOverlays.get(panelId) ?? []
      list.push(overlayId)
      panelToOverlays.set(panelId, list)
    }
  }

  const byName = new Map<string, Set<string>>()
  for (const element of collectFormLayoutRowElements(formRoot)) {
    const name = String(element.name ?? '').trim()
    const panelId = String(element.panelId ?? '').trim()
    if (!name || !panelId) continue
    const overlayIds = panelToOverlays.get(panelId)
    if (!overlayIds?.length) continue
    let pages = byName.get(name)
    if (!pages) {
      pages = new Set<string>()
      byName.set(name, pages)
    }
    for (const overlayId of overlayIds) pages.add(overlayId)
  }

  const result = new Map<string, string[]>()
  for (const [name, pages] of byName) {
    result.set(name, [...pages])
  }
  return result
}

const overlayLayoutDuplicatedFieldNamesCache = new WeakMap<object, Set<string>>()

/** Имена, которые встречаются на двух и более overlay-страницах формы. */
export function overlayLayoutDuplicatedFieldNames(formRoot: unknown): Set<string> {
  if (!formRoot || typeof formRoot !== 'object') return new Set()
  const cached = overlayLayoutDuplicatedFieldNamesCache.get(formRoot)
  if (cached) return cached
  const dupes = new Set<string>()
  for (const [name, pages] of collectOverlayFieldNamePages(formRoot)) {
    if (pages.length > 1) dupes.add(name)
  }
  overlayLayoutDuplicatedFieldNamesCache.set(formRoot, dupes)
  return dupes
}

/**
 * Id панелей из layout-ref `conditionalPanel` (+ ветки `subPanels`).
 * После getForm — также панели с `_attributes.subPanels` / `conditional_branch_code`.
 * Их row-элементы не участвуют в проверке уникальности name.
 */
export function collectConditionalPanelIds(formRoot: unknown): Set<string> {
  const result = new Set<string>()
  const layout = resolveFormLayoutRoot(formRoot)
  if (!layout) return result

  forEachCompactLayoutCell(layout, (cell) => {
    const panelId = readCellConditionalPanelId(cell)
    if (!panelId) return
    addConditionalPanelWithBranches(result, panelId, readConditionalPanelRefAttributes(cell))
  })

  const panelsMap = layout.panels
  if (panelsMap && typeof panelsMap === 'object' && !Array.isArray(panelsMap)) {
    for (const [panelKey, panelData] of Object.entries(panelsMap as Record<string, unknown>)) {
      if (!panelData || typeof panelData !== 'object') continue
      const panelRecord = panelData as Record<string, unknown>
      const attrs = panelRecord._attributes as Record<string, unknown> | undefined
      if (!attrs || typeof attrs !== 'object') continue
      const hasConditionalMarker =
        attrs.subPanels != null
        || attrs.conditional_branch_code != null
        || attrs.conditionalBranchCode != null
      if (!hasConditionalMarker) continue
      const panelId = String(attrs.id ?? panelKey).trim()
      addConditionalPanelWithBranches(result, panelId, attrs)
    }
  }

  return result
}

const pushLayoutElement = (
  result: FormLayoutElementRef[],
  elementConfig: Record<string, unknown>,
  panelId: string | null,
): void => {
  const name = readElementName(elementConfig)
  if (!name) return
  const attributes = elementConfig._attributes as { id?: unknown } | undefined
  result.push({
    id: tryParseElementId(attributes?.id ?? elementConfig.id),
    name,
    type: readElementType(elementConfig),
    panelId,
  })
}

const collectPanelLayoutElements = (
  panelRecord: Record<string, unknown>,
  panelId: string | null,
  options: { includeIdentificators: boolean },
): FormLayoutElementRef[] => {
  const result: FormLayoutElementRef[] = []

  const rows = panelRecord.rows
  if (Array.isArray(rows)) {
    for (const rowItem of rows) {
      if (!Array.isArray(rowItem)) continue
      for (const cellItem of rowItem) {
        const elementConfig = (cellItem as { element?: Record<string, unknown> })?.element
        if (!elementConfig || typeof elementConfig !== 'object') continue
        pushLayoutElement(result, elementConfig, panelId)
      }
    }
  }

  if (options.includeIdentificators) {
    const identificators = panelRecord.identificators
    if (identificators != null) {
      const list = Array.isArray(identificators) ? identificators : [identificators]
      for (const item of list) {
        if (!item || typeof item !== 'object') continue
        pushLayoutElement(result, item as Record<string, unknown>, panelId)
      }
    }
  }

  return result
}

const collectFormLayoutElementsInternal = (
  formRoot: unknown,
  options: { includeIdentificators: boolean },
): FormLayoutElementRef[] => {
  const layout = resolveFormLayoutRoot(formRoot)
  const panelsMap = layout?.panels
  if (!panelsMap || typeof panelsMap !== 'object' || Array.isArray(panelsMap)) {
    return []
  }

  const result: FormLayoutElementRef[] = []

  for (const [panelKey, panelData] of Object.entries(panelsMap as Record<string, unknown>)) {
    if (!panelData || typeof panelData !== 'object') continue
    const panelRecord = panelData as Record<string, unknown>
    const panelId = String(
      (panelRecord._attributes as { id?: unknown } | undefined)?.id ?? panelKey,
    ).trim() || null

    result.push(...collectPanelLayoutElements(panelRecord, panelId, options))
  }

  return result
}

/** Элементы из panels.*.rows (проверка уникальности name в разметке). */
export function collectFormLayoutRowElements(formRoot: unknown): FormLayoutElementRef[] {
  return collectFormLayoutElementsInternal(formRoot, { includeIdentificators: false })
}

/** Все элементы из panels.*.rows и panels.*.identificators (layout-scoped поля). */
export function collectFormLayoutElements(formRoot: unknown): FormLayoutElementRef[] {
  return collectFormLayoutElementsInternal(formRoot, { includeIdentificators: true })
}

export function findFormElementNameConflicts(
  elements: readonly FormLayoutElementRef[],
): FormElementNameConflict[] {
  const byName = new Map<string, FormLayoutElementRef[]>()
  for (const element of elements) {
    const bucket = byName.get(element.name)
    if (bucket) {
      bucket.push(element)
    } else {
      byName.set(element.name, [element])
    }
  }

  return [...byName.entries()]
    .filter(([, refs]) => refs.length > 1)
    .map(([name, refs]) => ({ name, elements: refs }))
}

export function formatFormElementNameConflict(conflict: FormElementNameConflict): string {
  const details = conflict.elements
    .map((element) => {
      const idPart = element.id != null ? `id ${element.id}` : 'без id'
      const typePart = element.type ? `, ${element.type}` : ''
      const panelPart = element.panelId ? `, panel ${element.panelId}` : ''
      return `${idPart}${typePart}${panelPart}`
    })
    .join('; ')
  return `имя «${conflict.name}»: ${details}`
}

export function validateFormElementNames(formRoot: unknown): FormElementNameConflict[] {
  const skippedPanelIds = new Set([
    ...collectHiddenPanelIds(formRoot),
    ...collectConditionalPanelIds(formRoot),
  ])
  const elements = collectFormLayoutRowElements(formRoot).filter(
    (element) => !element.panelId || !skippedPanelIds.has(element.panelId),
  )

  const overlayPagePanels = collectOverlayPagePanelIds(formRoot)
  const overlayPanelIds = new Set<string>()
  for (const panelIds of overlayPagePanels.values()) {
    for (const panelId of panelIds) overlayPanelIds.add(panelId)
  }

  const conflicts: FormElementNameConflict[] = []
  for (const panelIds of overlayPagePanels.values()) {
    const overlayElements = elements.filter(
      (element) => element.panelId != null && panelIds.has(element.panelId),
    )
    conflicts.push(...findFormElementNameConflicts(overlayElements))
  }

  const rest = elements.filter(
    (element) => !element.panelId || !overlayPanelIds.has(element.panelId),
  )
  conflicts.push(...findFormElementNameConflicts(rest))
  return conflicts
}

export function reportFormElementNameConflicts(
  conflicts: readonly FormElementNameConflict[],
  options?: { formId?: string | number | null },
): void {
  if (!conflicts.length) return

  const formId = String(options?.formId ?? '').trim() || 'unknown'
  const reportKey = `${formId}|${conflicts.map((c) => c.name).sort().join(',')}`
  if (reportedConflictKeys.has(reportKey)) return
  reportedConflictKeys.add(reportKey)

  const summary = conflicts.map(formatFormElementNameConflict).join('; ')
  const message =
    `Некорректная разметка формы ${formId}: несколько элементов с одинаковым name. ` +
    `Имена полей на форме должны быть уникальны. ${summary}`

  console.error(`[form-metadata] ${message}`)
  if (!import.meta.env.DEV) return
  useNotification().error({
    summary: 'Ошибка разметки формы',
    detail: message,
    life: 12_000,
  })
}
